import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  RocketLaunchIcon,
  PlusIcon,
  XMarkIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useWalletStore } from '../store/walletStore';
import { listPresales, createPresaleOps, displayToRaw } from '../api/vexforge';
import { signAndSend, ensureConnected } from '../lib/submit';

export default function Presales() {
  const { isConnected } = useWalletStore();
  const [presales, setPresales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    tokenMint: '',
    tokenAllocation: '',
    pricePerToken: '',
    minContribution: '',
    maxContribution: '',
    softCap: '',
    hardCap: '',
    startTime: '',
    endTime: '',
    lpPercent: '50',
    lpLockDuration: '2592000', // 30 days in seconds
  });

  useEffect(() => {
    async function fetchPresales() {
      try {
        const result = await listPresales(50, null);
        setPresales(Array.isArray(result) ? result : []);
      } catch (err) {
        console.error('Failed to fetch presales:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchPresales();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!isConnected) {
      await ensureConnected();
      return;
    }

    setCreating(true);
    try {
      const startTimestamp = Math.floor(new Date(form.startTime).getTime() / 1000);
      const endTimestamp = Math.floor(new Date(form.endTime).getTime() / 1000);
      if (!Number.isFinite(startTimestamp) || !Number.isFinite(endTimestamp)) {
        throw new Error('Start and end time are required');
      }

      // VXS-denominated fields are entered in VXS and signed as raw nanoVXS (9 dp) —
      // the unit VexSpark formats on its approval screen. Token allocation is raw.
      const ops = createPresaleOps({
        tokenMint: form.tokenMint,
        tokenAllocation: form.tokenAllocation,
        pricePerToken: displayToRaw(form.pricePerToken, 9, 'Price per token'),
        minContribution: displayToRaw(form.minContribution, 9, 'Min contribution'),
        maxContribution: displayToRaw(form.maxContribution, 9, 'Max contribution'),
        softCap: displayToRaw(form.softCap, 9, 'Soft cap'),
        hardCap: displayToRaw(form.hardCap, 9, 'Hard cap'),
        startTime: startTimestamp,
        endTime: endTimestamp,
        lpPercent: form.lpPercent,
        lpLockDuration: form.lpLockDuration,
      });
      const txHash = await signAndSend(ops, `Create presale for ${form.tokenMint}`);
      if (!txHash) return;

      toast.success(`Presale creation submitted (tx ${String(txHash).slice(0, 14)}…) — refresh once confirmed`);
      setShowCreate(false);
    } catch (err) {
      toast.error(err.message || 'Failed to create presale');
    } finally {
      setCreating(false);
    }
  };

  const statusBadge = (status) => {
    const styles = {
      Active: 'bg-vextaris-lime/20 text-vextaris-lime',
      Pending: 'bg-yellow-500/20 text-yellow-400',
      Finalized: 'bg-green-500/20 text-green-400',
      Cancelled: 'bg-red-500/20 text-red-400',
    };
    return styles[status] || 'bg-gray-500/20 text-gray-400';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Presale Hub</h1>
          <p className="text-gray-400 mt-1">Anti-rug presales with escrowed tokens and mandatory LP locks</p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-vextaris text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
        >
          {showCreate ? <XMarkIcon className="w-5 h-5" /> : <PlusIcon className="w-5 h-5" />}
          <span>{showCreate ? 'Cancel' : 'Create Presale'}</span>
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6 space-y-4">
          <h2 className="text-xl font-semibold text-white">New Presale</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Token Mint Address</label>
              <input type="text" name="tokenMint" value={form.tokenMint} onChange={handleChange} required
                placeholder="Vx1... or 0x..." className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple font-mono text-sm" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Token Allocation (raw base units)</label>
              <input type="text" name="tokenAllocation" value={form.tokenAllocation} onChange={handleChange} required
                placeholder="Amount to escrow" className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Price per Token (VXS)</label>
              <input type="text" name="pricePerToken" value={form.pricePerToken} onChange={handleChange} required
                placeholder="0.001" className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">LP Percent (%)</label>
              <input type="number" name="lpPercent" value={form.lpPercent} onChange={handleChange} required min="10" max="100"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white focus:outline-none focus:border-vextaris-purple" />
              <p className="text-xs text-gray-500 mt-1">Min 10%, funds auto-locked in LP</p>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Min Contribution (VXS)</label>
              <input type="text" name="minContribution" value={form.minContribution} onChange={handleChange} required
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Max Contribution (VXS)</label>
              <input type="text" name="maxContribution" value={form.maxContribution} onChange={handleChange} required
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Soft Cap (VXS)</label>
              <input type="text" name="softCap" value={form.softCap} onChange={handleChange} required
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Hard Cap (VXS)</label>
              <input type="text" name="hardCap" value={form.hardCap} onChange={handleChange} required
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Start Time</label>
              <input type="datetime-local" name="startTime" value={form.startTime} onChange={handleChange} required
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white focus:outline-none focus:border-vextaris-purple" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">End Time</label>
              <input type="datetime-local" name="endTime" value={form.endTime} onChange={handleChange} required
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white focus:outline-none focus:border-vextaris-purple" />
            </div>
          </div>

          <button type="submit" disabled={creating}
            className="px-6 py-2 bg-gradient-vextaris text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50">
            {creating ? 'Creating...' : 'Create Presale'}
          </button>
        </form>
      )}

      {/* Presale Cards */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading presales...</div>
      ) : presales.length === 0 ? (
        <div className="bg-vextaris-dark/50 rounded-2xl border border-vextaris-purple/20 p-12 text-center">
          <RocketLaunchIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400">No presales found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {presales.map((ps, i) => {
            const progress = ps.hard_cap ? Math.min((ps.total_raised / ps.hard_cap) * 100, 100) : 0;
            const mint = ps.token_mint || ps.presale_mint || '';

            return (
              <Link
                key={i}
                to={`/presale/${mint}`}
                className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6 hover:border-vextaris-purple/40 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-semibold">{ps.token_name || `Token ${mint.slice(0, 8)}...`}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${statusBadge(ps.status)}`}>
                    {ps.status || 'Unknown'}
                  </span>
                </div>

                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Progress</span>
                    <span>{ps.total_raised || 0} / {ps.hard_cap || '--'} VXS</span>
                  </div>
                  <div className="w-full h-2 bg-vextaris-darker rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-vextaris rounded-full" style={{ width: `${progress}%` }} />
                  </div>
                </div>

                <div className="flex items-center space-x-4 text-xs text-gray-400">
                  <div className="flex items-center space-x-1">
                    <ClockIcon className="w-3 h-3" />
                    <span>{ps.end_time ? new Date(ps.end_time * 1000).toLocaleDateString() : '--'}</span>
                  </div>
                  <span>Price: {ps.price_per_token || '--'} VXS</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}